/**
 * POS Log Mistake Component
 * Allows staff to log order mistakes with PIN authentication.
 * Staff pays 25% of the item cost (75% discount) which goes to their tab.
 * 
 * Features:
 * - PIN entry for staff who made the mistake
 * - Item selection from menu or manual entry
 * - Mistake type selection
 * - Customer impact / disposition tracking
 * - Automatic 25% charge calculation
 * - Integration with staff tabs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, AlertTriangle, Search, Plus, Minus, Loader2,
  User, Package, DollarSign, CheckCircle, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api, posApi } from '@/utils/apiClient';

// Mistake type options
const MISTAKE_TYPES = [
  { id: 'wrong_item', name: 'Wrong Item Served', severity: 'medium' },
  { id: 'extra_item', name: 'Extra/Unwanted Item', severity: 'low' },
  { id: 'wrong_customization', name: 'Wrong Customization', severity: 'medium' },
  { id: 'forgot_item', name: 'Forgot Item', severity: 'low' },
  { id: 'dropped_spilled', name: 'Dropped/Spilled', severity: 'high' },
  { id: 'wrong_table', name: 'Served to Wrong Table', severity: 'medium' },
  { id: 'double_order', name: 'Duplicate Order Entry', severity: 'medium' },
  { id: 'other', name: 'Other Mistake', severity: 'medium' }
];

const CUSTOMER_IMPACTS = [
  { id: 'replaced', description: 'Order item replaced' },
  { id: 'refunded', description: 'Customer refunded' },
  { id: 'kept_anyway', description: 'Customer kept item anyway' },
  { id: 'discarded', description: 'Item discarded' },
  { id: 'comped', description: 'Item comped to customer' }
];

const DISPOSITIONS = [
  { id: 'staff_kept', description: 'Staff kept the item' },
  { id: 'discarded', description: 'Item was discarded/wasted' },
  { id: 'given_to_customer', description: 'Given to customer' },
  { id: 'returned_to_kitchen', description: 'Returned to kitchen' }
];

export default function POSLogMistake({ staffId, onClose }) {
  const [step, setStep] = useState('select-staff'); // select-staff, select-items, review
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  
  // Staff selection
  const [staffPin, setStaffPin] = useState('');
  const [mistakeStaff, setMistakeStaff] = useState(null);
  const [verifyingPin, setVerifyingPin] = useState(false);
  
  // Selected items
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Mistake details
  const [mistakeType, setMistakeType] = useState('wrong_item');
  const [customerImpact, setCustomerImpact] = useState('replaced');
  const [disposition, setDisposition] = useState('staff_kept');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  
  // Staff charge percentage (configurable, defaults to 25%)
  const STAFF_CHARGE_PERCENT = 25;
  
  // Fetch menu items
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await api.get('/menu');
        setMenuItems(res.data.filter(item => item.is_available !== false));
      } catch (err) {
        console.error('Failed to fetch menu:', err);
        toast.error('Error loading menu items');
      }
    };
    fetchMenu();
  }, []);
  
  // Verify staff PIN
  const verifyStaffPin = async () => {
    if (staffPin.length !== 6) {
      toast.error('PIN must be 6 digits');
      return;
    }
    
    setVerifyingPin(true);
    try {
      const res = await posApi.post('/pos/login', { pin: staffPin });
      setMistakeStaff(res.data.staff);
      setStep('select-items');
      toast.success(`Staff verified: ${res.data.staff.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid PIN');
    } finally {
      setVerifyingPin(false);
    }
  };
  
  // Handle PIN input
  const handlePinInput = (digit) => {
    if (staffPin.length < 6) {
      setStaffPin(prev => prev + digit);
    }
  };
  
  const handlePinClear = () => {
    setStaffPin('');
  };
  
  const handlePinBackspace = () => {
    setStaffPin(prev => prev.slice(0, -1));
  };
  
  // Add item to selection
  const addItem = (item) => {
    const existing = selectedItems.find(i => i.item_id === item.id);
    if (existing) {
      setSelectedItems(prev => prev.map(i => 
        i.item_id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setSelectedItems(prev => [...prev, {
        item_id: item.id,
        item_name: item.name,
        item_price: item.price || item.sizes?.[0]?.price || 0,
        quantity: 1
      }]);
    }
  };
  
  // Update item quantity
  const updateItemQuantity = (itemId, delta) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };
  
  // Remove item
  const removeItem = (itemId) => {
    setSelectedItems(prev => prev.filter(i => i.item_id !== itemId));
  };
  
  // Calculate totals
  const itemsTotal = selectedItems.reduce((sum, item) => sum + (item.item_price * item.quantity), 0);
  const staffCharge = itemsTotal * (STAFF_CHARGE_PERCENT / 100);
  const businessLoss = itemsTotal - staffCharge;
  
  // Submit mistake log
  const submitMistakeLog = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    if (!description.trim()) {
      toast.error('Please enter a description of what happened');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        staff_id: mistakeStaff.id,
        table_number: tableNumber ? parseInt(tableNumber) : null,
        mistake_type: mistakeType,
        items: selectedItems,
        description: description.trim(),
        customer_impact: customerImpact,
        disposition: disposition,
        logged_by_staff_id: staffId,
        notes: notes.trim() || null
      };
      
      const res = await posApi.post('/mistakes/log', payload);
      
      if (res.data.success) {
        toast.success(`Mistake logged: ${res.data.mistake.incident_id}`, {
          description: `Pending manager approval. Staff charge: $${staffCharge.toFixed(2)}`
        });
        onClose();
      } else {
        toast.error(res.data.message || 'Failed to log mistake');
      }
    } catch (err) {
      console.error('Error logging mistake:', err);
      toast.error(err.response?.data?.detail || 'Failed to log mistake');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter menu items
  const filteredMenu = menuItems.filter(item => 
    item.name.toLowerCase().includes(menuSearch.toLowerCase()) ||
    item.category?.toLowerCase().includes(menuSearch.toLowerCase())
  );
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="pos-log-mistake-modal">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-amber-500 dark:bg-amber-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">Log Server Mistake</h2>
              <p className="text-amber-100 text-sm">Staff pays 25% of item cost</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-amber-600 dark:hover:bg-amber-700">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Select Staff */}
          {step === 'select-staff' && (
            <div className="space-y-6">
              <div className="text-center">
                <User className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Who made the mistake?</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Enter the staff member&apos;s 6-digit PIN</p>
              </div>
              
              {/* PIN Display */}
              <div className="flex justify-center gap-2">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold ${
                      staffPin.length > i 
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-gray-900 dark:text-white' 
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                    }`}
                  >
                    {staffPin.length > i ? '*' : ''}
                  </div>
                ))}
              </div>
              
              {/* Number Pad */}
              <div className="max-w-xs mx-auto grid grid-cols-3 gap-3">
                {[1,2,3,4,5,6,7,8,9].map(n => (
                  <Button
                    key={n}
                    variant="outline"
                    className="h-14 text-xl font-semibold"
                    onClick={() => handlePinInput(String(n))}
                  >
                    {n}
                  </Button>
                ))}
                <Button variant="outline" className="h-14" onClick={handlePinClear}>Clear</Button>
                <Button variant="outline" className="h-14 text-xl font-semibold" onClick={() => handlePinInput('0')}>0</Button>
                <Button variant="outline" className="h-14" onClick={handlePinBackspace}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <Button 
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                onClick={verifyStaffPin}
                disabled={staffPin.length !== 6 || verifyingPin}
              >
                {verifyingPin ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Staff'
                )}
              </Button>
            </div>
          )}
          
          {/* Step 2: Select Items */}
          {step === 'select-items' && (
            <div className="space-y-4">
              {/* Staff Info */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <span className="font-medium text-gray-900 dark:text-white">{mistakeStaff?.name}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({mistakeStaff?.role})</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setStep('select-staff'); setStaffPin(''); setMistakeStaff(null); }}>
                  Change
                </Button>
              </div>
              
              {/* Search Menu */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search menu items..."
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Menu Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {filteredMenu.slice(0, 18).map(item => (
                  <button
                    key={item.id}
                    onClick={() => addItem(item)}
                    className="p-2 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{item.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">${(item.price || item.sizes?.[0]?.price || 0).toFixed(2)}</p>
                  </button>
                ))}
              </div>
              
              {/* Selected Items */}
              {selectedItems.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Selected Items:</h4>
                  <div className="space-y-2">
                    {selectedItems.map(item => (
                      <div key={item.item_id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{item.item_name}</p>
                          <p className="text-sm text-gray-500">${item.item_price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateItemQuantity(item.item_id, -1)}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-medium text-gray-900 dark:text-white">{item.quantity}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateItemQuantity(item.item_id, 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeItem(item.item_id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Continue Button */}
              <Button 
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => setStep('review')}
                disabled={selectedItems.length === 0}
              >
                Continue to Details
              </Button>
            </div>
          )}
          
          {/* Step 3: Review & Submit */}
          {step === 'review' && (
            <div className="space-y-4">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-gray-900 dark:text-white">{mistakeStaff?.name}</span>
                  </div>
                  <span className="px-2 py-1 bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 text-xs rounded-full">
                    {selectedItems.length} item(s)
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Item Total</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">${itemsTotal.toFixed(2)}</p>
                  </div>
                  <div className="border-x border-amber-200 dark:border-amber-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Staff Pays (25%)</p>
                    <p className="text-lg font-bold text-amber-600 dark:text-amber-400">${staffCharge.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Business Loss</p>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">${businessLoss.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              
              {/* Mistake Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mistake Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {MISTAKE_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setMistakeType(type.id)}
                      className={`p-2 text-left rounded-lg border transition-colors ${
                        mistakeType === type.id 
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <p className={`font-medium text-sm ${mistakeType === type.id ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>
                        {type.name}
                      </p>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        type.severity === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        type.severity === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' :
                        'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      }`}>
                        {type.severity}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Customer Impact */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Customer Impact</label>
                <select
                  value={customerImpact}
                  onChange={(e) => setCustomerImpact(e.target.value)}
                  className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                >
                  {CUSTOMER_IMPACTS.map(impact => (
                    <option key={impact.id} value={impact.id}>{impact.description}</option>
                  ))}
                </select>
              </div>
              
              {/* Disposition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">What happened to the item?</label>
                <select
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                  className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                >
                  {DISPOSITIONS.map(d => (
                    <option key={d.id} value={d.id}>{d.description}</option>
                  ))}
                </select>
              </div>
              
              {/* Table Number (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Table Number (optional)</label>
                <Input
                  type="number"
                  placeholder="e.g. 5"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">What happened? *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the mistake..."
                  className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 min-h-[80px]"
                  required
                />
              </div>
              
              {/* Notes (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Additional Notes (optional)</label>
                <Input
                  placeholder="Any additional notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep('select-items')}>
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={submitMistakeLog}
                  disabled={loading || !description.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Log Mistake
                    </>
                  )}
                </Button>
              </div>
              
              {/* Info Note */}
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                This will be sent to a manager for approval. Once approved, ${staffCharge.toFixed(2)} will be added to {mistakeStaff?.name}&apos;s tab.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
